import { QueryClient } from '@tanstack/react-query'
import axios from 'axios'

/**
 * TanStack Query client for the customer app.
 *
 * Web defaults differ from WebInternal on purpose:
 * - 2 minute stale time, 15 minute cache time
 * - one retry, only for failures a second attempt can fix (see shouldRetryQuery)
 * - no refetch on window focus (several Insights endpoints are slow and would
 *   refire on every tab switch); opt in per query where freshness matters
 * - refetch on reconnect
 *
 * Exported so non-React code (Pusher handler, logout, the 401 interceptor) can
 * call `setQueryData`, `invalidateQueries`, or `clear` on the same cache.
 */
const RETRYABLE_STATUSES = new Set([502, 503, 504])

/**
 * Retry a failed query once, and only when the request got no response (a
 * dropped connection or client timeout) or a gateway answered 502/503/504.
 * A 4xx (including the read-only 403) or a 500 fails on the first attempt: the
 * same request would fail again, and the backend's 20 s response timeout is a
 * 500, so retrying it doubled the wait before the error state showed.
 *
 * @param {number} failureCount - failed attempts so far
 * @param {unknown} error - the latest error
 * @returns {boolean}
 */
export const shouldRetryQuery = (failureCount, error) => {
  if (failureCount >= 1 || !axios.isAxiosError(error)) return false
  return !error.response || RETRYABLE_STATUSES.has(error.response.status)
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
})

/** Drop every cached server-state entry. Call on logout and on a 401. */
export const clearQueryCache = () => queryClient.clear()
